import { useDialog, H3, Button } from '@wheely/ui-kit';
import { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { isEmpty } from '../utils';

import { FilterItem } from './FilterItem';
import s from './FiltersDialog.module.css';
import { IFiltersProps } from './types';

interface IFiltersDialogProps<FilterName extends string> {
  filtersConfig: IFiltersProps<FilterName>['config'];
  onApplyFilters: (newValues: IFiltersProps<FilterName>['value']) => void;
  onRemoveFilters: () => void;
  initialValues: IFiltersProps<FilterName>['value'];
}

const FiltersDialog = <FilterName extends string>({
  filtersConfig,
  onApplyFilters,
  onRemoveFilters,
  initialValues,
  onClose,
}: IFiltersDialogProps<FilterName> & { onClose: () => void }) => {
  const [filterValues, setFilterValues] = useState(initialValues);

  const onChange: IFiltersProps<FilterName>['onChange'] = useCallback((_, { name, value }) => {
    setFilterValues(oldFilters => ({ ...oldFilters, [name]: value }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    onApplyFilters(filterValues);
    onClose();
  }, [filterValues, onApplyFilters, onClose]);

  const handleClearFilters = useCallback(() => {
    onRemoveFilters();
    onClose();
  }, [onRemoveFilters, onClose]);

  return (
    <>
      <div className={s.filtersList}>
        {filtersConfig.map(({ title, name, placeholder, options, type, icon, hasOneLineLabel }) => (
          <div className={s.item} key={name}>
            <FilterItem<FilterName>
              value={filterValues}
              onChange={onChange}
              title={title}
              name={name}
              placeholder={placeholder}
              options={options}
              type={type}
              icon={icon}
              hasOneLineLabel={hasOneLineLabel}
            />
          </div>
        ))}
      </div>
      <div className={s.footerButtons}>
        <Button onClick={handleApplyFilters} className={s.applyButton}>
          <FormattedMessage defaultMessage="Apply" id="vXHNL9" description="Apply button title" />
        </Button>
        {!isEmpty(filterValues) && (
          <Button onClick={handleClearFilters} className={s.clearButton} minimal={true}>
            <FormattedMessage
              defaultMessage="Clear filters"
              id="Zl67C/"
              description="Clear filters button title"
            />
          </Button>
        )}
      </div>
    </>
  );
};

const useFiltersDialog = <FilterName extends string>({
  filtersConfig,
  onApplyFilters,
  onRemoveFilters,
  initialValues,
}: IFiltersDialogProps<FilterName>) => {
  const { openDialog } = useDialog();

  const openFiltersDialog = () => {
    openDialog(
      ({ onClose }) => (
        <FiltersDialog<FilterName>
          filtersConfig={filtersConfig}
          initialValues={initialValues}
          onApplyFilters={onApplyFilters}
          onRemoveFilters={onRemoveFilters}
          onClose={onClose}
        />
      ),
      {
        title: (
          <H3>
            <FormattedMessage
              defaultMessage="Filter journeys"
              id="hDO1G9"
              description="Dialog title"
            />
          </H3>
        ),
        className: s.filtersDialog,
        titleClassName: s.filtersDialogTitle,
        bodyClassName: s.filtersDialogBody,
      },
    );
  };

  return { openModal: openFiltersDialog };
};

export type { IFiltersDialogProps };
export { FiltersDialog, useFiltersDialog };
